import { useId } from 'react';

import { cn } from '@nesso/ui/lib/utils';
import { Field, FieldContent, FieldLabel, FieldTitle } from '@nesso/ui/primitives/field';
import { RadioGroupItem } from '@nesso/ui/primitives/radio-group';

const sizeVariants = {
    default: 'h-12.5',
    sm: 'h-9.5',
} as const;

type Props = {
    title: string;
    value: string;
    className?: string;
    size?: keyof typeof sizeVariants;
};

export function RadioButtonItem({ title, value, className, size = 'default' }: Props) {
    const id = useId();

    return (
        <FieldLabel
            htmlFor={id}
            className={cn(
                'relative cursor-pointer [&:has([disabled])]:cursor-not-allowed [&:has([disabled])]:pointer-events-none [&:has([disabled])]:opacity-50 hover:bg-accent/50 hover:text-accent-foreground',
                'has-[:focus-visible]:border-ring has-[:focus-visible]:[box-shadow:0_0_0_2px_var(--accent)]',
                className,
            )}
        >
            <Field orientation="horizontal" className={sizeVariants[size]}>
                <FieldContent className="self-center">
                    <FieldTitle>{title}</FieldTitle>
                    {/* opacity-0 keeps the control focusable and in the a11y tree: `hidden` removed it from the
                        tab order, and `sr-only` is absolutely positioned inside the flex content and shifts the
                        title. The overlay is out of flow, so layout is untouched; the focus ring is on the label. */}
                    <RadioGroupItem
                        value={value}
                        id={id}
                        className="absolute inset-0 opacity-0 disabled:opacity-0"
                    />
                </FieldContent>
            </Field>
        </FieldLabel>
    );
}
